import React, { useContext } from 'react';
import { AngularContext } from 'react-app';
import { config } from '@config/pendo.config.json';

import useAngularExpression from 'shared/hooks/use-angular-expression';
import shouldShowAiAssignmentFeedback from '../../services/should-show-ai-assignment-feedback';

import AiInsightsPanel from './ai-insights-panel';
import CombinedTabs from './combined-tabs';
import Skeleton from './skeleton';
import SkillsRatingForm from '../skills-rating/skills-rating-form';
import SkillsRatingTabs from '../skills-rating/skills-rating-tabs';
import submissionRightColumnStyles from './styles';

const SubmissionRightColumn: React.FC = () => {
  const { $scope, injectServices } = useContext(AngularContext);
  const vm = ($scope as any)?.vm;

  const [
    ReportsManager,
    CurrentUserManager,
    CurrentPermissionsManager,
    InstitutionsManager,
  ] = injectServices([
    'ReportsManager',
    'CurrentUserManager',
    'CurrentPermissionsManager',
    'InstitutionsManager',
  ]);

  const showAi = useAngularExpression(() => shouldShowAiAssignmentFeedback({
    ReportsManager,
    CurrentUserManager,
    CurrentPermissionsManager,
    InstitutionsManager,
  }));
  const hasSkills = useAngularExpression(
    () => !!vm?.ReportsManager?.currentReport?.hasSkillsRating?.(),
  );
  const isRatingView = useAngularExpression(() => !!vm?.isRatingView?.());
  const hasPendingApproval = useAngularExpression(() => !!vm?.hasPendingApproval?.());
  const showSkills = hasSkills && !hasPendingApproval;

  const isLoadingSubmission = useAngularExpression(
    () => !!vm?.ReportsManager?.isLoadingSubmission,
  );
  const stateReportId = useAngularExpression(() => vm?.$stateParams?.reportId);
  const currentReportId = useAngularExpression(
    () => vm?.ReportsManager?.currentReport?.id,
  );
  const isTransitioning = isLoadingSubmission
    || (!!stateReportId && !!currentReportId && String(stateReportId) !== String(currentReportId));

  let content: React.ReactNode = null;

  if (isTransitioning) {
    content = <Skeleton />;
  } else if (isRatingView) {
    content = <SkillsRatingForm />;
  } else if (showAi && showSkills) {
    content = <CombinedTabs />;
  } else if (showAi) {
    content = <AiInsightsPanel />;
  } else if (showSkills) {
    content = <SkillsRatingTabs />;
  }

  if (!content) return null;

  return (
    <div
      css={submissionRightColumnStyles}
      data-qa={config.pendo.submissionView.rightColumn.container}
    >
      {content}
    </div>
  );
};

export default SubmissionRightColumn;
